import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DEFAULT_PAGE } from '../../../../constants/pagination';
import FILTER_CONSTANTS, { FILTER_VALUES } from '../../../../constants/FilterConstants';
import { defaultApprovedFilterState } from '../../../../redux-slice/weeklyGLPostingFilters';
import { useGetApprovedWeeklyGLPostingsQuery } from '../../../../services/WeeklyGLPosting/WeeklyGLPostingService';
import { useGetGLPostingProductCategoriesQuery } from '../../../../services/Sublocation/SublocationService';
import { useGetDropdownUsersQuery } from '../../../../services/Users/UsersService';
import { routeConstants } from '../../../../constants/routeConstants';
import {
  transformPostingData,
  sanitizeSearchText,
  createStaticDataFetcher,
  getBackendSortField,
  getFrontendSortField,
  parseSortString,
  buildSortString,
  buildPostingCacheKey,
  getSelectedIds,
  resolveX3ProductName,
  transformProductCategories,
} from '../../utils/glPostingMethods';
import { usePostingTableControls } from '../../hooks/usePostingTableControls';

const APPROVED_FILTER_CONFIG = FILTER_CONSTANTS.FILTER_DATA.APPROVED_GL_POSTING;
const DROPDOWN_PER_PAGE = 1000;

export const useApprovedPostingTab = ({ filters: filtersFromRedux, setFilters, resetFilters }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const filters = useMemo(() => ({ ...defaultApprovedFilterState, ...(filtersFromRedux || {}) }), [filtersFromRedux]);

  const { data: productCategoriesData } = useGetGLPostingProductCategoriesQuery();
  const { data: usersData } = useGetDropdownUsersQuery({ page: DEFAULT_PAGE, perPage: DROPDOWN_PER_PAGE });
  const safeProductCategories = useMemo(
    () => (Array.isArray(productCategoriesData) ? productCategoriesData : []),
    [productCategoriesData],
  );
  const safeUsers = useMemo(() => (Array.isArray(usersData?.users) ? usersData.users : []), [usersData?.users]);

  const productCategoriesMap = useMemo(() => {
    if (safeProductCategories.length === 0) return {};
    return safeProductCategories.reduce((acc, category) => {
      const categoryId = String(category?.id ?? '');
      const x3ProductName = resolveX3ProductName(category);
      if (!categoryId) return acc;
      acc[categoryId] = category;
      if (x3ProductName) {
        acc[`x3:${x3ProductName}`] = category;
      }
      return acc;
    }, {});
  }, [safeProductCategories]);

  const usersMap = useMemo(() => {
    if (safeUsers.length === 0) return {};
    return safeUsers.reduce((acc, user) => {
      acc[String(user?.id ?? '')] = user;
      return acc;
    }, {});
  }, [safeUsers]);

  const productTypeOptions = useMemo(() => transformProductCategories(safeProductCategories), [safeProductCategories]);

  const postedByOptions = useMemo(() => {
    const unique = new Map();
    safeUsers.forEach((user) => {
      const id = String(user?.id ?? '');
      const firstName = user?.firstName ?? '';
      const lastName = user?.lastName ?? '';
      const name = `${firstName} ${lastName}`.trim();
      if (id && name && !unique.has(id)) {
        unique.set(id, { id, name });
      }
    });
    return Array.from(unique.values()).sort((a, b) =>
      (a?.name ?? '').localeCompare(b?.name ?? '', 'en', { sensitivity: 'base' }),
    );
  }, [safeUsers]);

  const filterApiMap = useMemo(
    () => ({
      [FILTER_VALUES.CREATED_BY]: createStaticDataFetcher(postedByOptions),
      [FILTER_VALUES.PRODUCT_TYPE]: createStaticDataFetcher(productTypeOptions),
    }),
    [postedByOptions, productTypeOptions],
  );

  const selectedStartDate = filters?.[FILTER_VALUES.START_DATE] || '';
  const selectedEndDate = filters?.[FILTER_VALUES.END_DATE] || '';
  const selectedProductNames = useMemo(
    () =>
      Array.from(
        new Set(
          getSelectedIds(filters[FILTER_VALUES.PRODUCT_TYPE])
            .map((selectedId) => productTypeOptions.find((option) => option.id === selectedId)?.x3ProductName)
            .filter(Boolean),
        ),
      ),
    [filters, productTypeOptions],
  );

  const filterData = useMemo(
    () =>
      APPROVED_FILTER_CONFIG.FILTER?.map((item) => {
        if (item.value === FILTER_VALUES.START_DATE) {
          return {
            ...item,
            maxDate: selectedEndDate || undefined,
          };
        }
        if (item.value === FILTER_VALUES.END_DATE) {
          return {
            ...item,
            minDate: selectedStartDate || undefined,
          };
        }
        return filterApiMap[item.value] ? { ...item, api: filterApiMap[item.value] } : item;
      }) || [],
    [filterApiMap, selectedStartDate, selectedEndDate],
  );

  const queryParams = useMemo(() => {
    const params = {
      page: filters.pageNumber,
      perPage: filters.rowsPerPage,
    };

    if (selectedProductNames.length > 0) {
      params.x3ProductNames = selectedProductNames;
    }

    const selectedCreatedByIds = getSelectedIds(filters[FILTER_VALUES.CREATED_BY]);
    if (selectedCreatedByIds.length > 0) {
      params.createdBy = selectedCreatedByIds;
    }

    if (selectedStartDate) {
      params.startDate = selectedStartDate;
    }

    if (selectedEndDate) {
      params.endDate = selectedEndDate;
    }

    if (filters.searchText) {
      params.search = filters.searchText.trim();
    }

    if (filters.sort) {
      const { sortBy, sortOrder } = parseSortString(filters.sort);
      params.sortBy = sortBy;
      params.sortOrder = sortOrder;
    }

    return params;
  }, [filters, selectedStartDate, selectedEndDate, selectedProductNames]);

  const { data: apiData, isLoading, isFetching, isError, error } = useGetApprovedWeeklyGLPostingsQuery(queryParams);

  const transformCacheRef = useRef(new Map());
  const cacheSignatureRef = useRef({
    productCategoriesMap,
    usersMap,
  });

  useEffect(() => {
    const signature = cacheSignatureRef.current;
    if (signature.productCategoriesMap !== productCategoriesMap || signature.usersMap !== usersMap) {
      transformCacheRef.current.clear();
      cacheSignatureRef.current = {
        productCategoriesMap,
        usersMap,
      };
    }
  }, [productCategoriesMap, usersMap]);

  const tableData = useMemo(() => {
    const postings = Array.isArray(apiData?.postings) ? apiData.postings : [];
    const nextCache = new Map();
    const rows = postings.map((posting) => {
      const cacheKey = buildPostingCacheKey(posting);
      const cached = transformCacheRef.current.get(cacheKey);
      if (cached) {
        nextCache.set(cacheKey, cached);
        return cached;
      }
      const transformed = transformPostingData(posting, usersMap, productCategoriesMap);
      nextCache.set(cacheKey, transformed);
      return transformed;
    });
    transformCacheRef.current = nextCache;
    return rows;
  }, [apiData?.postings, usersMap, productCategoriesMap]);

  const totalCount = apiData?.pagination?.total || 0;

  const {
    handlePageChange,
    handlePerPageChange,
    handleSearch,
    handleResetSearch,
    handleApplyFilter: handleApplyFilterBase,
    handleResetFilter,
  } = usePostingTableControls({
    filters,
    setFilters,
    resetFilters,
    dispatch,
    sanitizeSearchText,
  });

  const handleApplyFilterWithDateValidation = useCallback(
    (filterValues) => {
      const nextStartDate = filterValues?.[FILTER_VALUES.START_DATE] || '';
      const nextEndDate = filterValues?.[FILTER_VALUES.END_DATE] || '';
      const parsedStartDate = nextStartDate ? dayjs(nextStartDate) : null;
      const parsedEndDate = nextEndDate ? dayjs(nextEndDate) : null;

      if (parsedStartDate?.isValid() && parsedEndDate?.isValid() && parsedEndDate.isBefore(parsedStartDate, 'day')) {
        handleApplyFilterBase({
          ...filterValues,
          [FILTER_VALUES.END_DATE]: '',
        });
        return;
      }

      handleApplyFilterBase(filterValues);
    },
    [handleApplyFilterBase],
  );

  const handleRowClick = useCallback(
    (rowData) => {
      if (!rowData?.id) return;
      navigate(
        `/${routeConstants.WEEKLY_GL_POSTING_ROUTE}/${routeConstants.WEEKLY_GL_POSTING_APPROVED}/${rowData.id}`,
        {
          state: {
            isApproved: true,
            selectedRow: {
              postingId: rowData?.postingId,
              createdBy: rowData?.createdBy,
              postedWeek: rowData?.postedWeek,
              productType: rowData?.productType,
            },
          },
        },
      );
    },
    [navigate],
  );

  const handleSort = useCallback(
    (columnAccessor, isDesc) => {
      const backendField = getBackendSortField(columnAccessor);
      if (!backendField) return;

      const sortString = buildSortString(columnAccessor, isDesc);
      dispatch(
        setFilters({
          ...filters,
          sort: sortString,
          pageNumber: DEFAULT_PAGE,
        }),
      );
    },
    [dispatch, filters, setFilters],
  );

  const sortState = useMemo(() => {
    const sortString = filters.sort || defaultApprovedFilterState.sort;
    const { sortBy, sortOrder } = parseSortString(sortString);
    const field = getFrontendSortField(sortBy);
    return {
      id: field,
      desc: sortOrder === 'DESC',
    };
  }, [filters.sort]);

  return {
    filters,
    filterData,
    tableData,
    totalCount,
    isLoading,
    isFetching,
    isError,
    error,
    handlePageChange,
    handlePerPageChange,
    handleSearch,
    handleResetSearch,
    handleApplyFilter: handleApplyFilterWithDateValidation,
    handleResetFilter,
    handleRowClick,
    handleSort,
    sortState,
  };
};
